import { LogIn, Mic, RotateCcw } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef } from 'react';
import type { User } from '../../shared/events.ts';
import type { UserMemory } from '../../shared/memory.ts';
import { checkInDue, openRound } from '../../shared/rounds.ts';
import { cn } from '../lib/cn.ts';
import { RegistryItem } from '../registry/index.tsx';
import type { ErrorKind, Snapshot } from '../session/conversation.ts';
import { SetupFlow } from '../setup/SetupFlow.tsx';
import type { MemoryProblem } from '../shell/useMemory.tsx';
import { Composer } from './Composer.tsx';
import { Orb, type OrbState } from './Orb.tsx';
import { Transcript } from './Transcript.tsx';
import { WarmupSteps, warmupSteps } from './WarmupSteps.tsx';

function orbState(s: Snapshot): OrbState {
  if (s.phase === 'error') return 'error';
  if (s.phase === 'ended') return 'ended';
  if (s.phase === 'starting' || s.phase === 'reconnecting') return 'connecting';
  if (s.phase === 'idle') return 'idle';
  if (s.busyTools.length) return 'working';
  return s.modelSpeaking ? 'speaking' : 'listening';
}

const ERROR_STATUS: Record<ErrorKind, string> = {
  mic_denied: 'Microphone blocked',
  mic_system: 'Microphone blocked',
  mic_busy: 'Microphone busy',
  mic_missing: 'No microphone found',
};

const STATUS: Record<OrbState, string> = {
  idle: '',
  connecting: 'Connecting…',
  listening: 'Listening',
  speaking: 'Speaking',
  working: 'Working on it…',
  ended: 'Session ended',
  error: 'Something went wrong',
};

const WARMUPS: Record<number, string> = { 1: 'One quick warm-up', 2: 'Two quick warm-ups', 3: 'Three quick warm-ups' };

/** The line under the orb. What the user is waiting on, in their words: never internal tool names. */
export function statusText(snap: Snapshot, setupDue: boolean): string {
  const state = orbState(snap);
  // Done was tapped: the engine keeps the recording TaskView until the review returns, so check the item's phase.
  if (snap.phase === 'live' && snap.items.some((i) => i.task?.phase === 'processing')) return 'Reviewing your answer…';
  // The review is on the card; the task stays open (and its TaskView "recording") until the scores land.
  if (snap.phase === 'live' && snap.items.some((i) => i.task?.scoresPending)) return 'Scoring your speech…';
  if (snap.phase === 'reconnecting') return 'Reconnecting…';
  if (snap.phase === 'error' && snap.errorKind) return ERROR_STATUS[snap.errorKind];
  if (snap.task && state === 'listening')
    return snap.task.recording ? 'Recording your answer' : 'Your turn, answer out loud';
  if (state === 'idle' && setupDue) return 'A few details first';
  return STATUS[state];
}

/**
 * Under "Connecting…" while the browser's mic prompt is open, for someone who hasn't answered it yet: a denial there
 * ends the call on the recovery screen. Never on a mock call, which doesn't open the mic.
 */
export function micHint(snap: Snapshot, permission: PermissionState | null): string | null {
  // No button named: the labels differ by browser (Chrome's "Allow while visiting the site"), and "Allow" can be read
  // inside "Don't allow".
  return snap.phase === 'starting' && snap.askingMic && permission === 'prompt'
    ? 'When your browser asks for the microphone, allow it.'
    : null;
}

/** The post-call "what I learned" card; keyed by session so a slow completion of call N can't hide call N+1's. */
export type CallSummary = {
  sessionId: string;
  status: 'saving' | 'done';
  items: Array<{ label: string; value: string }>;
};

export type ConversationActions = {
  start(): void;
  end(): void;
  sendText(text: string): void;
  pick(itemId: string, option: { id: string; label: string }): void;
  finishTask(): void;
  skipTask(): void;
  setupFinished(): void;
};

/**
 * The Chat tab as a pure view of a conversation snapshot and memory. ConversationView drives it from the live engine;
 * the Lab drives it from fixtures, so every state can be seen without a call.
 */
export function ConversationScreen({
  user,
  snap,
  memory,
  loaded,
  checkInOn = false,
  micPermission = null,
  loadProblem = null,
  retryingLoad = false,
  onRetryLoad,
  summary,
  actions,
  onOpenProfile,
}: {
  user: User;
  snap: Snapshot;
  memory: UserMemory;
  loaded: boolean;
  /** Check-ins (flow 002) are on for this user: home offers one when it's due. */
  checkInOn?: boolean;
  /** The browser's microphone permission (useMicPermission), for the hint while its prompt is open. */
  micPermission?: PermissionState | null;
  /** Memory hasn't loaded and the last read failed: say why, instead of a home screen for the wrong stage. */
  loadProblem?: MemoryProblem | null;
  retryingLoad?: boolean;
  onRetryLoad?: () => void;
  summary: CallSummary | null;
  actions: ConversationActions;
  onOpenProfile?: () => void;
}) {
  const live = snap.phase === 'starting' || snap.phase === 'live' || snap.phase === 'reconnecting';
  const stage = memory.journey.stage;
  // During a call, what the server planned it for. This tab's memory can be a call behind (another tab, or the
  // catch-up at this start, completed one), and the header must match the call, not the stale stage.
  const callGoal = live ? snap.plan?.main : undefined;
  // Call 2's assessment set, as a warm-up: on the home screen before it, and in the header during it.
  const call2 = callGoal ? callGoal === 'call2' : stage === 'context_ready';
  // A check-in (flow 002): during a call the plan says so; at home, when it's on for them and one is due, the same
  // rule the server plans by (`checkInDue`). Its steps are the round's: the call's start begins one.
  const checkIn = callGoal ? callGoal === 'reassessment' : checkInOn && checkInDue(memory, new Date());
  const warmup = call2
    ? warmupSteps(memory, snap.items)
    : checkIn
      ? warmupSteps(memory, snap.items, openRound(memory)?.items ?? {})
      : null;
  const warmupsLeft = warmup?.filter((s) => s.state === 'todo').length ?? 0;
  // Also after a call that failed late: the stage moved on, so the next step is setup, not the pilot again.
  const setupDue = stage === 'call1_done';
  const state = orbState(snap);
  const hint = micHint(snap, micPermission);
  const bottom = useRef<HTMLDivElement>(null);
  const active = snap.phase === 'live' || snap.phase === 'reconnecting';
  const role = memory.fields['interview.role']?.value;
  const startLabel =
    snap.phase === 'error'
      ? 'Try again'
      : snap.phase === 'ended'
        ? 'Start another call'
        : stage === 'context_ready'
          ? warmupsLeft
            ? 'Start warm-up'
            : 'Start practice round'
          : checkIn
            ? 'Start check-in'
            : role
              ? 'Start practising'
              : 'Start talking';
  // A start that failed (no session) puts its fix first; a call that failed after going live leaves setup first.
  const showSetup = setupDue && !(snap.phase === 'error' && !snap.sessionId);
  // Docked in thumb reach on phones, but never over the cards a call left behind or the setup below.
  const dockStart = snap.items.length === 0 && !setupDue;

  useEffect(() => {
    if (snap.items.length) bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [snap.items.length]);

  return (
    <div
      data-testid="conversation"
      data-phase={snap.phase}
      data-session={snap.sessionId ?? ''}
      data-goal={snap.plan?.main ?? ''}
      className="flex min-h-dvh flex-col"
    >
      {/* Sticky, and filled so cards scroll under it cleanly, only during a call. Elsewhere it would cover the cards
          and the form, and a fill would cut a band out of the page glow. */}
      <header
        className={cn(
          'z-10 flex flex-col items-center gap-3 px-4 pb-6 pt-8',
          live ? 'sticky top-0 bg-gradient-to-b from-bg from-80% to-transparent' : 'relative',
        )}
      >
        <Orb state={state} size={snap.items.length ? 'sm' : 'lg'} />
        {/* Empty on the home screen, where the heading and button already say what to do. */}
        <p data-testid="status" className="min-h-5 text-sm text-subtle">
          {statusText(snap, setupDue)}
        </p>
        {hint ? (
          <p data-testid="mic-hint" className="-mt-1 max-w-xs text-balance text-center text-sm text-muted">
            {hint}
          </p>
        ) : null}
        {warmup && live ? <WarmupSteps steps={warmup} compact label={checkIn ? 'Check-in' : 'Warm-up'} /> : null}
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 space-y-4 px-4 pb-44">
        {loadProblem && !live ? (
          <div
            data-testid="memory-failed"
            data-problem={loadProblem}
            className="flex flex-col items-center gap-5 pt-10 text-center"
          >
            <h1 className="text-balance text-3xl font-semibold tracking-tight text-fg sm:text-4xl">
              {loadProblem === 'signed-out' ? 'You’ve been signed out.' : 'I couldn’t load your profile.'}
            </h1>
            <p className="max-w-md text-balance text-subtle">
              {loadProblem === 'signed-out' ? 'Sign in again to pick up where you left off.' : 'Try again in a moment.'}
            </p>
            <button
              type="button"
              data-testid="memory-retry"
              onClick={onRetryLoad}
              disabled={retryingLoad}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-fg px-6 py-3 text-[15px] font-semibold text-bg transition enabled:hover:scale-[1.02] disabled:opacity-60"
            >
              {loadProblem === 'signed-out' ? <LogIn className="size-4" /> : <RotateCcw className="size-4" />}
              {loadProblem === 'signed-out' ? 'Sign in again' : retryingLoad ? 'Trying…' : 'Try again'}
            </button>
          </div>
        ) : null}
        {/* Wait for memory: the home screen depends on the journey stage. */}
        {loaded && (snap.phase === 'idle' || snap.phase === 'ended' || snap.phase === 'error') ? (
          <div data-testid="home" data-stage={stage} className="flex flex-col items-center gap-5 pt-10 text-center">
            <h1 className="text-balance text-3xl font-semibold tracking-tight text-fg sm:text-4xl">
              {snap.phase === 'error'
                ? snap.errorKind
                  ? 'I can’t hear you yet.'
                  : 'That call didn’t work.'
                : snap.phase === 'ended'
                  ? 'Good session.'
                  : setupDue
                    ? `Nice to meet you, ${user.firstName}.`
                    : stage === 'context_ready'
                      ? 'Now I have enough context.'
                      : checkIn
                        ? 'Time for a quick check-in.'
                        : role
                          ? `Welcome back, ${user.firstName}`
                          : `Hey ${user.firstName}, ready to prep?`}
            </h1>
            {snap.phase === 'error' && snap.errorKind ? (
              <MicHelp kind={snap.errorKind} />
            ) : (
              <p className="max-w-md text-balance text-subtle">
                {snap.phase === 'error'
                  ? snap.error
                  : setupDue
                    ? 'A few details and our next call can go much deeper.'
                    : snap.phase === 'ended'
                      ? 'Your cards from this call are below.'
                      : stage === 'context_ready'
                        ? warmupsLeft
                          ? `${WARMUPS[warmupsLeft] ?? 'A few quick warm-ups'} first, so I know where to help.`
                          : 'Let’s start your first practice round.'
                        : checkIn
                          ? warmupsLeft
                            ? `${WARMUPS[warmupsLeft] ?? 'A few quick warm-ups'} again, to see what’s changed since you started.`
                            : 'The warm-ups are done: one short call to see what’s changed.'
                          : role
                            ? `Pick up your ${role} prep where we left off.`
                            : 'Tell fuse what you are preparing for. It listens, asks the right questions, and builds your practice on screen.'}
              </p>
            )}
            {warmup && snap.phase !== 'error' ? (
              <WarmupSteps steps={warmup} label={checkIn ? 'Check-in' : 'Warm-up'} />
            ) : null}
            {/* Hidden while the call's summary is saving: starting now would begin with the old stage. */}
            {showSetup || summary?.status === 'saving' ? null : (
              <button
                type="button"
                data-testid="start-button"
                onClick={actions.start}
                className={cn(
                  'inline-flex items-center justify-center gap-2 rounded-full bg-fg px-6 py-3 text-[15px] font-semibold text-bg shadow-[0_0_40px_-8px_rgba(125,211,252,0.6)] transition hover:scale-[1.02]',
                  dockStart
                    ? 'fixed inset-x-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-20 py-3.5 sm:static sm:mt-2 sm:py-3'
                    : 'mt-2',
                )}
              >
                {snap.phase === 'error' ? <RotateCcw className="size-4" /> : <Mic className="size-4" />}
                {startLabel}
              </button>
            )}
            {summary ? (
              <section data-testid="learned" className="card mt-4 w-full text-left ring-1 ring-fg/10">
                <h2 className="text-xs font-medium uppercase tracking-wide text-subtle">
                  {summary.status === 'saving' ? 'Noting what I learned…' : 'What I learned this time'}
                </h2>
                {summary.status === 'done' ? (
                  summary.items.length ? (
                    <ul className="mt-3 space-y-1.5">
                      {summary.items.map((i) => (
                        <li key={i.label} className="flex gap-3 text-[15px]">
                          <span className="w-32 shrink-0 text-subtle">{i.label}</span>
                          <span className="text-fg/90">{i.value}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-subtle">Nothing new this time.</p>
                  )
                ) : null}
                {onOpenProfile && summary.status === 'done' ? (
                  <button
                    type="button"
                    data-testid="open-profile"
                    onClick={onOpenProfile}
                    className="mt-3 text-sm text-accent hover:underline"
                  >
                    See everything I know →
                  </button>
                ) : null}
              </section>
            ) : null}
            {setupDue ? <SetupFlow onFinished={actions.setupFinished} /> : null}
            {showSetup ? (
              <button type="button" data-testid="talk-now" onClick={actions.start} className="btn-quiet">
                Skip setup, talk now
              </button>
            ) : null}
          </div>
        ) : null}

        <AnimatePresence initial={false}>
          {snap.items.map((item) => (
            <motion.div
              key={item.id}
              data-testid="canvas-item"
              data-tool={item.tool}
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 26 }}
            >
              <RegistryItem
                tool={item.tool}
                params={item.params}
                picked={item.picked}
                onPick={(o) => actions.pick(item.id, o)}
                task={item.task}
                live={snap.task?.itemId === item.id ? snap.task : null}
                onDone={actions.finishTask}
                onSkip={actions.skipTask}
              />
            </motion.div>
          ))}
        </AnimatePresence>
        {/* Scrolled to on each new card. The margin (main's pb-44) keeps the card's end above the fixed composer, so
            Done and Skip are never under it. */}
        <div ref={bottom} className="scroll-mb-44" />
      </main>

      {active ? (
        <footer
          className={cn(
            'fixed inset-x-0 bg-gradient-to-t from-bg from-75% to-transparent px-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-10',
            live ? 'bottom-0' : 'bottom-16',
          )}
        >
          {/* The width of main's content (max-w-2xl less its padding), so the composer lines up with the cards. */}
          <div className="mx-auto w-full max-w-[40rem] space-y-3">
            <Transcript lines={snap.transcript} />
            {active ? <Composer onSend={actions.sendText} onEnd={actions.end} disabled={!active} /> : null}
          </div>
        </footer>
      ) : null}
      {snap.phase === 'starting' ? (
        <footer className="fixed inset-x-0 bottom-0 flex justify-center px-4 pb-[calc(2rem+env(safe-area-inset-bottom))]">
          <button type="button" data-testid="cancel-call" onClick={actions.end} className="btn-quiet bg-surface">
            Cancel
          </button>
        </footer>
      ) : null}
    </div>
  );
}

type Platform = { ua: string; touchPoints: number; coarse: boolean };

function platform(): Platform {
  if (typeof navigator === 'undefined') return { ua: '', touchPoints: 0, coarse: false };
  const coarse = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
  return { ua: navigator.userAgent, touchPoints: navigator.maxTouchPoints ?? 0, coarse };
}

/** What's wrong and how to fix it, in the words of the browser they're on. */
export function micSteps(kind: ErrorKind, p: Platform): { problem: string; fix: string } {
  if (kind === 'mic_missing')
    return {
      problem: 'No microphone was found.',
      fix: 'Connect one, or pick another in your system settings, then try again.',
    };
  if (kind === 'mic_busy')
    return {
      problem: 'Your microphone is busy or not responding.',
      fix: 'Close other apps that use it, such as a video call, then try again.',
    };
  if (kind === 'mic_system') {
    const mac = 'On a Mac, turn the browser on in System Settings › Privacy & Security › Microphone.';
    const windows = 'On Windows, allow it in Settings › Privacy & security › Microphone.';
    const other = 'Allow the browser to use the microphone in your device’s privacy settings.';
    const where = /Windows/.test(p.ua) ? windows : /Macintosh/.test(p.ua) && !p.touchPoints ? mac : other;
    return {
      problem: 'Your computer is blocking this browser from using the microphone.',
      fix: `${where} Then try again.`,
    };
  }
  // iPadOS reports a Mac user agent; touch points give it away.
  const ios = /iPhone|iPad/.test(p.ua) || (/Macintosh/.test(p.ua) && p.touchPoints > 1);
  // Safari by what it sends, not by what it lacks: in-app browsers (LinkedIn, Gmail) have no aA menu.
  const safari =
    /Version\/[\d.]+.*Safari\//.test(p.ua) && !/CriOS|FxiOS|EdgiOS|Chrome|Chromium|Edg\/|Firefox/.test(p.ua);
  const problem = 'Your browser is blocking the microphone for this site.';
  if (ios && safari)
    return {
      problem,
      fix: 'Tap aA in the address bar, then Website Settings, and set Microphone to Allow. Then try again.',
    };
  if (ios)
    return {
      problem,
      fix: 'In Settings, let this browser use the microphone. Then reload this page and allow it when asked.',
    };
  if (/Macintosh/.test(p.ua) && safari)
    return {
      problem,
      fix: 'In the menu bar, choose Safari › Settings for This Website, and set Microphone to Allow. Then try again.',
    };
  if (p.coarse)
    return {
      problem,
      fix: 'Tap the icon at the left of the address bar, then Permissions, and allow the microphone. Then try again.',
    };
  return { problem, fix: 'Click the icon at the left of the address bar, set Microphone to Allow, then try again.' };
}

function MicHelp({ kind }: { kind: ErrorKind }) {
  const { problem, fix } = micSteps(kind, platform());
  return (
    <div data-testid="mic-help" className="max-w-md space-y-2 text-balance text-subtle">
      <p>{problem}</p>
      <p className="text-fg/85">{fix}</p>
    </div>
  );
}
